import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Bell } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";

export function NotificationsBell() {
  const [count, setCount] = useState(0);
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const timer = useRef(null);
  const nav = useNavigate();

  const load = async () => {
    try {
      const [c, list] = await Promise.all([
        api.get("/notifications/count"),
        api.get("/notifications"),
      ]);
      setCount(c.data.count || 0);
      setItems(list.data || []);
    } catch {}
  };

  useEffect(() => {
    load();
    timer.current = setInterval(load, 15000);
    return () => clearInterval(timer.current);
  }, []);

  const accept = async (n) => {
    try {
      const { data } = await api.post(`/notifications/${n.notification_id}/accept`);
      toast.success("On the way — good luck!");
      setOpen(false);
      load();
      if (data.room_id) nav(`/room/${data.room_id}`);
    } catch { toast.error("Could not accept"); }
  };
  const decline = async (n) => {
    try {
      await api.post(`/notifications/${n.notification_id}/decline`);
      load();
    } catch {}
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button className="relative p-2 rounded-full hover:bg-secondary transition" data-testid="notifications-bell" aria-label="Notifications">
          <Bell className="w-4 h-4" />
          {count > 0 && (
            <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 rounded-full bg-[color:hsl(var(--amber))] text-[10px] font-bold text-[color:hsl(var(--amber-foreground))] px-1 grid place-items-center">
              {count > 9 ? "9+" : count}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-96 p-0 max-h-[70vh] overflow-y-auto" align="end">
        <div className="p-3 border-b flex items-center justify-between">
          <div className="font-display font-bold">Notifications</div>
          <span className="text-xs text-muted-foreground">{count} unread</span>
        </div>
        {items.length === 0 ? (
          <div className="p-6 text-center text-sm text-muted-foreground" data-testid="notifications-empty">Nothing yet.</div>
        ) : (
          <div className="divide-y">
            {items.map((n) => (
              <div key={n.notification_id} className={`p-3 ${n.read ? "" : "bg-[color:hsl(var(--amber))]/5"}`} data-testid={`notification-${n.notification_id}`}>
                {n.kind === "help_invitation" ? (
                  <>
                    <div className="text-xs font-mono-label text-[color:hsl(var(--teal))]">{n.category} · help needed</div>
                    <div className="mt-1 text-sm"><b>{n.from_name}</b> — {n.preview}</div>
                    {n.match_reason?.length > 0 && (
                      <div className="mt-1 text-[11px] text-muted-foreground">Matched: {n.match_reason.join(", ")}</div>
                    )}
                    {n.status === "pending" ? (
                      <div className="mt-2 flex gap-2">
                        <Button size="sm" className="btn-teal h-8 rounded-full" onClick={() => accept(n)} data-testid={`accept-${n.notification_id}`}>Join</Button>
                        <Button size="sm" variant="ghost" className="h-8" onClick={() => decline(n)} data-testid={`decline-${n.notification_id}`}>Not now</Button>
                      </div>
                    ) : (
                      <div className="mt-2 text-[11px] font-mono-label text-muted-foreground">{n.status}</div>
                    )}
                  </>
                ) : (
                  <Link to={n.room_id ? `/room/${n.room_id}` : "/home"} onClick={() => setOpen(false)}>
                    <div className="text-xs font-mono-label text-[color:hsl(var(--amber))]">{n.kind.replace(/_/g, " ")}</div>
                    <div className="mt-1 text-sm">{n.preview}</div>
                  </Link>
                )}
              </div>
            ))}
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
